import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { MilestoneAndTeamCounts, MilestoneStatusCounts } from '../../lib/projectDashboard';
import { UNASSIGNED_TEAM_KEY } from '../../lib/projectDashboard';
import type { ProjectMilestone } from '../../graphql/types';
import { computeXAxisTickPlan } from '../../lib/chartTicks';
import './ProgressBurnChart.css';

export interface ProgressBurnChartProps {
  statusCounts: MilestoneStatusCounts[];
  milestones: ProjectMilestone[];
  /** Team-segmented counts (issue #494). When omitted, the chart only offers
   * the status segmentation and the mode toggle is not shown. */
  teamCounts?: MilestoneAndTeamCounts;
}

type SegmentBy = 'status' | 'team';

/** Flat row d3.stack operates on: `milestoneId` plus one numeric field per
 * stack key (status key or team key, depending on segmentation mode). */
interface StackDatum {
  milestoneId: string;
  [key: string]: number | string;
}

const WIDTH = 500;
const BASE_HEIGHT = 220;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 40 };
const LEGEND_ITEM_WIDTH = 110;
const LEGEND_ROW_HEIGHT = 16;

const STATUS_COLORS = {
  planned: '#9e9e9e',
  in_progress: '#1f77b4',
  done: '#2ca02c',
} as const;

type StatusKey = keyof typeof STATUS_COLORS;
const STACK_KEYS: StatusKey[] = ['planned', 'in_progress', 'done'];

function statusColor(key: StatusKey): string {
  if (key === 'planned') return STATUS_COLORS.planned;
  if (key === 'in_progress') return STATUS_COLORS.in_progress;
  return STATUS_COLORS.done;
}

// Colorblind-safe palette (Tableau 10), scales to many teams via modulo.
const TEAM_PALETTE = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
];
const UNASSIGNED_TEAM_COLOR = '#9e9e9e';

/** Deterministic color per team key: same team always gets the same color
 * (index-based on the sorted `teams` list), with a fixed fallback color for
 * unassigned/unresolved services regardless of how many real teams exist. */
function teamColor(teamKey: string, teamKeysInOrder: string[]): string {
  if (teamKey === UNASSIGNED_TEAM_KEY) return UNASSIGNED_TEAM_COLOR;
  const idx = teamKeysInOrder.indexOf(teamKey);
  return TEAM_PALETTE[idx % TEAM_PALETTE.length] ?? UNASSIGNED_TEAM_COLOR;
}

export function ProgressBurnChart({
  statusCounts,
  milestones,
  teamCounts,
}: ProgressBurnChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [segmentBy, setSegmentBy] = useState<SegmentBy>('status');

  const milestoneNameById = new Map(milestones.map((m) => [m.entityId, m.name]));
  const milestoneDateById = new Map(milestones.map((m) => [m.entityId, m.targetDate ?? null]));
  const isEmpty = milestones.length === 0;
  const activeMode: SegmentBy = teamCounts ? segmentBy : 'status';

  useEffect(() => {
    if (!svgRef.current || isEmpty) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = BASE_HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const isTeamMode = activeMode === 'team' && teamCounts;

    const milestoneIds = isTeamMode
      ? teamCounts.milestoneCounts.map((mc) => mc.milestoneId)
      : statusCounts.map((sc) => sc.milestoneId);

    const stackKeys: string[] = isTeamMode
      ? teamCounts.teams.map((t) => t.teamKey)
      : [...STACK_KEYS];

    const teamNameByKey = new Map(
      isTeamMode ? teamCounts.teams.map((t) => [t.teamKey, t.teamName]) : []
    );

    const stackData: StackDatum[] = isTeamMode
      ? teamCounts.milestoneCounts.map((mc) => {
          const row: StackDatum = { milestoneId: mc.milestoneId };
          stackKeys.forEach((key) => {
            // key is a stack key derived from teamCounts.teams (Team.entityId
            // or UNASSIGNED_TEAM_KEY) — not raw external input.
            // eslint-disable-next-line security/detect-object-injection
            row[key] = mc.counts[key] ?? 0;
          });
          return row;
        })
      : statusCounts.map((sc) => ({
          milestoneId: sc.milestoneId,
          planned: sc.planned,
          in_progress: sc.in_progress,
          done: sc.done,
        }));

    const stack = d3.stack<StackDatum>().keys(stackKeys);
    const series = stack(stackData);

    const xScale = d3.scaleBand().domain(milestoneIds).range([0, innerW]).padding(0.25);

    const totalByMilestone = new Map(
      stackData.map((d) => [
        d.milestoneId,
        // eslint-disable-next-line security/detect-object-injection
        stackKeys.reduce((sum, key) => sum + (Number(d[key]) || 0), 0),
      ])
    );
    const maxTotal = d3.max([...totalByMilestone.values()]) ?? 1;

    const yScale = d3.scaleLinear().domain([0, maxTotal]).range([innerH, 0]).nice();

    // Axes — decluttered per #496: skip factor derived from available width
    // vs. milestone count (falls back to one tick per year for quarter-style
    // milestone sets), rather than one tick per milestone.
    const tickPlan = computeXAxisTickPlan(
      milestoneIds.map((id) => ({
        id,
        name: milestoneNameById.get(id) ?? id,
        date: milestoneDateById.get(id),
      })),
      innerW
    );
    const xAxis = d3
      .axisBottom(xScale)
      .tickValues(tickPlan.tickIds)
      .tickFormat((id) => tickPlan.labelFor(id));
    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(xAxis)
      .attr('color', 'var(--forge-theme-text-medium, #757575)')
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('transform', tickPlan.rotate ? 'rotate(-30)' : null)
      .attr('text-anchor', tickPlan.rotate ? 'end' : 'middle');

    g.append('g')
      .call(d3.axisLeft(yScale).ticks(4))
      .attr('color', 'var(--forge-theme-text-medium, #757575)');

    // Stacked bars
    series.forEach((layer) => {
      const key = layer.key;
      const color = isTeamMode ? teamColor(key, stackKeys) : statusColor(key as StatusKey);
      const label = isTeamMode ? (teamNameByKey.get(key) ?? key) : key.replace('_', ' ');

      g.selectAll(`.bar-${key}`)
        .data(layer)
        .enter()
        .append('rect')
        .attr('class', `bar-${key}`)
        .attr('x', (d) => xScale(d.data.milestoneId) ?? 0)
        .attr('y', (d) => yScale(d[1]))
        .attr('height', (d) => Math.max(0, yScale(d[0]) - yScale(d[1])))
        .attr('width', xScale.bandwidth())
        .attr('fill', color)
        .attr('opacity', 0.85)
        .append('title')
        .text(
          (d) =>
            `${milestoneNameById.get(d.data.milestoneId) ?? d.data.milestoneId} — ${label}: ${d[1] - d[0]}`
        );
    });

    // Legend — wraps into multiple rows when there are many teams, and the
    // svg height grows to fit so the legend never overlaps the axis.
    const itemsPerRow = Math.max(1, Math.floor(innerW / LEGEND_ITEM_WIDTH));
    const legendRows = Math.max(1, Math.ceil(stackKeys.length / itemsPerRow));
    const totalHeight = BASE_HEIGHT + (legendRows - 1) * LEGEND_ROW_HEIGHT;
    svg.attr('height', totalHeight);

    const legendG = svg
      .append('g')
      .attr('transform', `translate(${MARGIN.left}, ${BASE_HEIGHT - 12})`);
    stackKeys.forEach((key, i) => {
      const col = i % itemsPerRow;
      const row = Math.floor(i / itemsPerRow);
      const lx = col * LEGEND_ITEM_WIDTH;
      const ly = row * LEGEND_ROW_HEIGHT;
      const color = isTeamMode ? teamColor(key, stackKeys) : statusColor(key as StatusKey);
      const label = isTeamMode ? (teamNameByKey.get(key) ?? key) : key.replace('_', ' ');

      legendG
        .append('rect')
        .attr('x', lx)
        .attr('y', ly)
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', color)
        .attr('rx', 2);
      legendG
        .append('text')
        .attr('x', lx + 16)
        .attr('y', ly + 10)
        .attr('font-size', '10px')
        .attr('fill', 'var(--forge-theme-text-high, #212121)')
        .text(label);
    });
  }, [statusCounts, teamCounts, milestones, isEmpty, activeMode]);

  const ForgeButton = 'forge-button' as React.ElementType;
  const ariaLabel =
    activeMode === 'team'
      ? 'Assignment progress burn chart showing counts per assigned team per milestone'
      : 'Assignment progress burn chart showing planned, in-progress, and done counts per milestone';

  return (
    <div className="progress-burn-chart">
      {teamCounts && (
        <div className="progress-burn-mode-toggle" role="group" aria-label="Segment chart by">
          <ForgeButton
            type="button"
            variant={segmentBy === 'status' ? 'raised' : 'outlined'}
            onClick={() => setSegmentBy('status')}
          >
            Status
          </ForgeButton>
          <ForgeButton
            type="button"
            variant={segmentBy === 'team' ? 'raised' : 'outlined'}
            onClick={() => setSegmentBy('team')}
          >
            Team
          </ForgeButton>
        </div>
      )}
      {isEmpty ? (
        <p className="progress-burn-empty forge-typography--body2">No assignments yet.</p>
      ) : (
        <svg ref={svgRef} width={WIDTH} height={BASE_HEIGHT} role="img" aria-label={ariaLabel} />
      )}
    </div>
  );
}
